import { Switch } from '@headlessui/react'
import CountrySelector from 'components/CountrySelector'
import { Country } from 'components/CountrySelector/countries'
import FormInput from 'components/Forms/form-input'
import GenericError from 'components/GenericError/GenericError'
import StaffSelector from 'components/StaffSelector'
import TimezoneSelector from 'components/TimezoneSelector'
import { Timezone } from 'components/TimezoneSelector/zones'
import {
    DEFAULT_ERROR_STATE,
    IError,
    useAppContext,
} from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import { MouseEvent, useEffect, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { CognitoErrorTypes } from 'types/cognito'
import { WORD_SINGLE_LINE_REGEX, PHONE_REGEX, ZIP_REGEX } from 'types/common'
import { classNames } from 'utils/dom-helpers'
import { useApiRequest, useFetch } from 'utils/fetch-helper'
import { getTranslation } from 'utils/language-helper'
import { FetchMethods } from 'utils/types'
import ImageFileUploader from '../../FileUploader/Image'
import Translation from '../../Translation'
import { SubmitError } from '../types'
import { GeneralFormValues } from './types'

function GeneralForm() {
    const { getApiRequest } = useApiRequest()
    const ctx = useAuth()
    const { translations } = useAppContext()
    const { getLocations, tenant } = useTenantContext()
    const { attributes, setAttributes, close } = ctx.LocationModal
    const [loading, toggleLoading] = useState(false)
    const [online, toggleOnline] = useState(false)
    const [success, setSuccess] = useState(false)
    const [staff, setStaff] = useState()
    const [location_image, setLocationImage] = useState('')
    const [show_upload_view, setShowUploadView] = useState(true)
    const [err, setErr] = useState<IError>(DEFAULT_ERROR_STATE)

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
        setError,
        reset,
        setValue,
    } = useForm<GeneralFormValues>({
        mode: 'onSubmit',
    })

    register('manager', { required: true })

    useEffect(() => {
        setValue('manager', (attributes?.manager as Record<string, string>)?.id as string)
        setValue('country', attributes?.country as string)
        setValue('timezone', attributes?.timezone as string)
        toggleOnline(attributes?.online as boolean)
    }, [])

    
    const api_fetch = useFetch(
        attributes?.id
            ? `/v1/locations/${attributes?.id}`
            : '/v1/locations',
        attributes?.id ? FetchMethods.PUT : FetchMethods.POST,
        false
    )

    function updateList() {
        ctx.LocationModal.setAttributes({
            has_updates: true,
        })
    }

    function handleCloseModal(e: MouseEvent<HTMLButtonElement>) {
        setAttributes({})
        close()
    }

    function handleOnline(val: boolean) {
        toggleOnline(val)
    }

    function handleCountryChange(v: Country) {
        setValue('country', v.name, { shouldValidate: true })
    }

    function handleManagerChange(v: Record<string, string>) {
        setValue('manager', v.id, { shouldValidate: true })
    }

    function handleTimezoneChange(v: Timezone) {
        setValue('timezone', v.text, { shouldValidate: true })
    }

    const onSubmit: SubmitHandler<GeneralFormValues> = async (
        values: GeneralFormValues
    ) => {
        toggleLoading(true)
        try {
            const {
                name,
                notes,
                timezone,
                phone,
                street_1,
                street_2,
                zip,
                city,
                state,
                country,
            } = values

            const res: any = await api_fetch.doFetch({
                country,
                imageUrl: location_image,
                state,
                tenant,
                timezone,
                name,
                manager: {
                    id: values.manager,
                },
                streetAddress1: street_1,
                streetAddress2: street_2,
                city,
                zip,
                online,
                phone,
                notes,
            })

            if (res?.ok) {
                updateList()
                reset()
                setSuccess(true)
                ctx.LocationModal.close()
            } else {
                setErr({ message: res?.message || 'Error!', tag: res?.tag })
            }
        } catch (e: unknown) {
            const { name, message } = e as SubmitError
            if (name == CognitoErrorTypes.UserExistsException) {
                setErr({ message })
                setError('name', {
                    type: CognitoErrorTypes.UserExistsException,
                    message,
                })
            }
        }
        toggleLoading(false)
        getLocations()
    }

    useEffect(() => {
        if (attributes?.image_url && !location_image) {
            setLocationImage(attributes?.image_url as string)
            setShowUploadView(false)
        }
    }, [attributes])

    useEffect(() => {
        async function retrieveStaffList() {
            const { content } = await getApiRequest(
                `/v1/staff/?tenantId=${tenant?.id}&size=500`
            )
            if (content) {
                setStaff(
                    content.map(
                        (st: Record<string, Record<string, string>>) => ({
                            id: st.id,
                            first_name: st.user.firstName,
                            last_name: st.user.lastName,
                        })
                    )
                )
            }
        }
        if (!staff && tenant?.id) {
            retrieveStaffList()
        }
    }, [staff, attributes])

    function UrlPreviewComponent(props: any) {
        return (
            <div className="image-upload-location">
                <div className="img-overlay">
                    <img
                        src={props.image_url}
                        className="rounded-lg imagepre"
                        alt={props.image_alt} 
                    />
                     <button
                            onClick={() => setShowUploadView(true)}
                            className="flex gap-3 items-center upload-btn  justify-center"
                        >
                            <i className="feather-edit " />
                            {/* <span>{translations['replace_photo']}</span> */}
                        </button>

                </div>
                {/* <div className="col-span-4">
                    <p className="text-lg my-2">{props.image_alt}</p>
                    <div className="flex gap-3">
                        <button
                            onClick={() => setShowUploadView(true)}
                            className="flex gap-3 items-center bg-primary-light text-white px-6 py-3 rounded-lg text-lg justify-center"
                        >
                            <i className="feather-image text-xl" />
                            <span>{translations['replace_photo']}</span>
                        </button>
                    </div>
                </div> */}
            </div>
        )
    }

    function showUploadView() {
        if (!show_upload_view) {
            return (
                <UrlPreviewComponent
                    image_url={location_image as string}
                    image_alt={translations['booking_background_img']}
                />
            )
        } else {
            return (
                <ImageFileUploader
                    tenant_id={tenant?.id}
                    accept=".jpeg, .jpg, .gif, .png"
                    onAdd={handleOnAdd}
                    form_context={{
                        attributes: attributes || {},
                        setAttributes: setAttributes as unknown as (
                            data: Record<string, string | File>
                        ) => void,
                    }}
                    addClassNames={{
                        dropzone:
                            'bg-transparent p-0 overflow-hidden h-64 border-0',
                        inputLabel:
                            'text-gray-400 font-medium text-lg flex-grow',
                        submitButtonContainer: 'mt-3',
                        submitButton:
                            'flex gap-3 items-center bg-primary-light text-white px-6 py-3 rounded-lg w-full text-lg font-serif font-light justify-center',
                    }}
                />
            )
        }
    }

    function handleOnAdd(image_url: string) {
        setLocationImage(image_url)
        setShowUploadView(false)
    }

    return (
        <form method="POST" onSubmit={handleSubmit(onSubmit)}>
            {!!err?.message?.length && (
                <GenericError
                    error_message={err?.message}
                    onCloseModal={() => setErr(DEFAULT_ERROR_STATE)}
                    tag={err?.tag}
                />
            )}
            <div className="lg:flex pb-6 gap-6">
                <div className="lg:w-1/2">
                    <div className="pb-6 ">
                        <fieldset className=" aot-form-group">
                            <FormInput
                                label='Location Name'
                                htmlFor='location-name-address'
                                fieldError={errors.name}
                                required
                            >
                                <input
                                    type="text"
                                    {...register('name', {
                                        required: "common.location_required",
                                        pattern: {
                                            value: WORD_SINGLE_LINE_REGEX,
                                            message: 'invalid.pattern.name'
                                        }
                                    })}
                                    defaultValue={
                                        (attributes?.name as string) || ''
                                    }
                                />
                            </FormInput>
                        </fieldset>
                    </div>
                    <div className="mb-6">
                        <label
                            htmlFor="online"
                            className={classNames(
                                'block text-sm',
                                errors.online?.type ? 'text-red-700' : ''
                            )}
                        >
                            Online
                        </label>
                        <Switch
                            name="online"
                            id="online"
                            checked={online}
                            onChange={handleOnline}
                            className={`${online ? 'bg-primary' : 'bg-gray-300'
                                }
        relative inline-flex mt-1 flex-shrink-0 h-5 w-10 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus-visible:ring-2  focus-visible:ring-white focus-visible:ring-opacity-75`}
                        >
                            <span className="sr-only">Use setting</span>
                            <span
                                aria-hidden="true"
                                className={`${online
                                    ? 'translate-x-5'
                                    : 'translate-x-0'
                                    }
        pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow-lg transform ring-0 transition ease-in-out duration-200`}
                            />
                        </Switch>
                    </div>
                </div>
                <div className="lg:w-1/2 w-full">
                    <Translation
                        translations={translations}
                        content_key="thumbnail_label"
                        render_as="label"
                        className="text-sm mb-1 block"
                    />
                    {showUploadView()}
                </div>
            </div>
            {online ? (
                ''
            ) : (
                <div className="flex gap-6 mb-6">
                    <fieldset className="lg:w-1/2 relative aot-form-group">
                        <FormInput
                            label='Street Address 1'
                            htmlFor='street-1'
                            fieldError={errors.street_1}
                            className="relative"
                            required
                        >
                            <input
                                type="text"
                                {...register('street_1', {
                                    required: "common.street_required",
                                    pattern: {
                                        value: WORD_SINGLE_LINE_REGEX,
                                        message: 'invalid.pattern.address'
                                    }
                                })}
                                defaultValue={
                                    (attributes?.street_1 as string) || ''
                                }
                            />
                        </FormInput>
                    </fieldset>
                    <fieldset className="lg:w-1/2 relative aot-form-group">
                        <FormInput
                            label='Street Address 2'
                            htmlFor='street-2'
                            fieldError={errors.street_2}
                        >
                            <input
                                type="text"
                                {...register('street_2', {
                                    pattern: {
                                        value: WORD_SINGLE_LINE_REGEX,
                                        message: 'invalid.pattern.address'
                                    }
                                })}
                                defaultValue={
                                    (attributes?.street_2 as string) || ''
                                }
                            />
                        </FormInput>
                    </fieldset>
                </div>
            )}
            <div className="flex gap-6 mb-6">
                <fieldset className="lg:w-1/2 relative aot-form-group">
                    <FormInput
                        label='City'
                        htmlFor='city'
                        fieldError={errors.city}
                        required
                    >
                        <input
                            type="text"
                            {...register('city', {
                                required: "common.city_required",
                                pattern: {
                                    value: WORD_SINGLE_LINE_REGEX,
                                    message: 'invalid.pattern.city'
                                }
                            })}
                            defaultValue={(attributes?.city as string) || ''}
                        />
                    </FormInput>
                </fieldset>
                {online ? (
                    ''
                ) : (
                    <fieldset className="lg:w-1/2  relative aot-form-group">
                        <FormInput
                            label='Zip'
                            htmlFor='zip'
                            fieldError={errors.zip}
                            required
                        >
                            <input
                                type="text"
                                {...register('zip', {
                                    required: "common.zip_required",
                                    pattern: {
                                        value: ZIP_REGEX,
                                        message: 'invalid.pattern.zip'
                                    }
                                })}
                                defaultValue={(attributes?.zip as string) || ''}
                            />
                        </FormInput>
                    </fieldset>
                )}
                <fieldset className="lg:w-1/2  relative aot-form-group">
                    <FormInput
                        label='State'
                        htmlFor='state'
                        fieldError={errors.state}
                    >
                        <input
                            type="text"
                            {...register('state', {
                                pattern: {
                                    value: WORD_SINGLE_LINE_REGEX,
                                    message: 'invalid.pattern.state'
                                }                            
                            })}
                            defaultValue={(attributes?.state as string) || ''}
                        />
                    </FormInput>
                </fieldset>
            </div>
            <div className="flex gap-6 mb-6">
                <fieldset className="w-1/2 relative aot-form-group">
                    <FormInput
                        label='Country'
                        htmlFor='country'
                        fieldError={errors.country}
                        required
                    >
                        <Controller
                            control={control}
                            name="country"
                            rules={{ required: 'common.country_required' }}
                            render={({ field: { value } }) => (
                                <CountrySelector
                                    className=''
                                    id="country"
                                    onChange={handleCountryChange}
                                    error={errors.country?.type}
                                    defaultValue={value as string} 
                                />
                            )}
                        />
                    </FormInput>
                </fieldset>
                <fieldset className="relative w-1/2 aot-form-group">
                    <FormInput
                        label='Timezone'
                        htmlFor='timezone'
                        fieldError={errors.timezone}
                        required
                    >
                        <Controller
                            control={control}
                            name="timezone"
                            rules={{ required: 'common.timezone_required' }}
                            render={({ field: { value } }) => (
                                <TimezoneSelector
                                    id="timezone"
                                    onChange={handleTimezoneChange}
                                    error={errors.timezone?.type}
                                    defaultValue={value as string}
                                />
                            )}
                        />
                        
                    </FormInput>
                </fieldset>
            </div>
            <div className="flex  gap-6">
                <fieldset className="w-1/2 aot-form-group pb-6">
                    <FormInput
                        label='Manager'
                        htmlFor='manager'
                        fieldError={errors.manager}
                        required_key="common.manager_required"
                        required
                    >
                        <StaffSelector
                            id="manager"
                            data={staff || []}
                            onChange={handleManagerChange}
                            error={errors.manager?.type}
                            defaultValue={
                                attributes?.manager as Record<string, string>
                            }
                        />
                    </FormInput>
                </fieldset>
                <fieldset className="w-1/2 aot-form-group pb-6">
                    <FormInput
                        label='Phone'
                        htmlFor='phone'
                        fieldError={errors.phone}
                        required
                    >
                        <input
                            type="text"
                            {...register('phone', {
                                required: "common.phone_required",
                                pattern: {
                                    value: PHONE_REGEX,
                                    message: 'invalid.pattern.phone'
                                }
                            })}
                            defaultValue={attributes?.phone as string || ''}
                        />
                    </FormInput>
                </fieldset>
            </div>
            <fieldset className="pb-6 aot-form-group">
                <FormInput
                    label='Notes'
                    htmlFor='notes'
                    fieldError={errors.notes}
                >
                    <textarea
                        className="placeholder-gray-300 mt-1 h-40 focus:ring-primary-light focus:border-primary-light block w-full  rounded-md "
                        placeholder={getTranslation(
                            'private_notes_location',
                            translations
                        )}
                        defaultValue={attributes?.notes as string}
                        {...register('notes', {
                            required: false,
                            maxLength: {
                                value: 2000,
                                message: "common.max_length_error"
                            }
                        })}
                    />
                </FormInput>
            </fieldset>
            <div>
                <div className="flex justify-end gap-4">
                    <button
                        type="button"
                        className="border border-gray-300 rounded-lg  inline-block aot-btn"
                        onClick={handleCloseModal}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className={classNames(
                            'group relative flex justify-center',
                            ' aot-btn border border-transparent',
                            'rounded-md text-white',
                            'focus:outline-none',
                            loading
                                ? 'bg-primary-light'
                                : 'bg-primary hover:bg-primary-dark focus:ring-2 focus:ring-offset-2 focus:ring-primary-light'
                        )}
                    >
                        Save
                    </button>
                </div>
            </div>
        </form>
    )
}

export default GeneralForm
